import { useState } from "react";
import { toast } from "sonner";
import { UserPlus, Plus, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/FormBits";

const empty = { username: "", password: "", name: "", email: "", phone: "", role: "customer", checking_balance: "0", investment_balance: "0" };

export default function CreateUserDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(empty);
  const [cards, setCards] = useState([]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const setCard = (i, k, v) => setCards((c) => c.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", {
        ...f, checking_balance: parseFloat(f.checking_balance) || 0, investment_balance: parseFloat(f.investment_balance) || 0,
        cards: cards.map((c) => ({ name: c.name, balance: parseFloat(c.balance) || 0, credit_limit: parseFloat(c.credit_limit) || 0 })),
      });
      toast.success(`User ${f.username} created`);
      setF(empty); setCards([]); setOpen(false); onCreated();
    } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-[var(--red)] hover:bg-[#a50d26] h-11 px-6" data-testid="admin-add-user-button"><UserPlus size={16} className="mr-2" /> Add user</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-[var(--navy)]">Create new user login</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="create-user-form">
          <Field label="Full name (shown in welcome)"><Input value={f.name} onChange={set("name")} required data-testid="new-user-name-input" /></Field>
          <Field label="Email"><Input type="email" value={f.email} onChange={set("email")} data-testid="new-user-email-input" /></Field>
          <Field label="Username"><Input value={f.username} onChange={set("username")} required data-testid="new-user-username-input" /></Field>
          <Field label="Password"><Input value={f.password} onChange={set("password")} required data-testid="new-user-password-input" /></Field>
          <Field label="Role">
            <Select value={f.role} onValueChange={(v) => setF((p) => ({ ...p, role: v }))}>
              <SelectTrigger data-testid="new-user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="customer">Customer</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Phone"><Input type="tel" value={f.phone} onChange={set("phone")} data-testid="new-user-phone-input" /></Field>
          <Field label="Checking balance ($)"><Input type="number" step="0.01" value={f.checking_balance} onChange={set("checking_balance")} data-testid="new-user-checking-input" /></Field>
          <Field label="Investment balance ($)"><Input type="number" step="0.01" value={f.investment_balance} onChange={set("investment_balance")} data-testid="new-user-investment-input" /></Field>
          <div className="sm:col-span-2 border-t pt-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-semibold text-slate-700">Credit cards</span>
              <Button type="button" variant="outline" size="sm" onClick={() => setCards([...cards, { name: "Maderian Rewards Visa", balance: "0", credit_limit: "25000" }])} data-testid="new-user-add-card-button"><Plus size={14} className="mr-1" /> Add card</Button>
            </div>
            {cards.map((c, i) => (
              <div key={i} className="grid grid-cols-[1.5fr_1fr_1fr_auto] gap-2 mb-2">
                <Input value={c.name} onChange={(e) => setCard(i, "name", e.target.value)} placeholder="Card name" data-testid={`new-card-name-${i}`} />
                <Input type="number" value={c.balance} onChange={(e) => setCard(i, "balance", e.target.value)} placeholder="Balance" data-testid={`new-card-balance-${i}`} />
                <Input type="number" value={c.credit_limit} onChange={(e) => setCard(i, "credit_limit", e.target.value)} placeholder="Limit" data-testid={`new-card-limit-${i}`} />
                <button type="button" onClick={() => setCards(cards.filter((_, j) => j !== i))} className="text-slate-400 hover:text-[var(--red)]"><X size={16} /></button>
              </div>
            ))}
          </div>
          <Button className="sm:col-span-2 rounded-full bg-[var(--blue)] hover:bg-[var(--navy)] h-11" data-testid="create-user-submit-button">Create user</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
